import { Interface, toQuantity, TransactionResponse } from 'ethers'

import AmbireAccount from '../../../contracts/compiled/AmbireAccount.json'
import AmbireFactory from '../../../contracts/compiled/AmbireFactory.json'
import ERC20 from '../../../contracts/compiled/IERC20.json'
import { Account, AccountOnchainState } from '../../interfaces/account'
import { Hex } from '../../interfaces/hex'
import { TxnRequest } from '../../interfaces/keystore'
import { Network } from '../../interfaces/network'
import { RPCProvider } from '../../interfaces/provider'
import { getSafeBroadcastTxn } from '../../libs/safe/safe'
import wait from '../../utils/wait'
import { AccountOp, GasFeePayment, getSignableCalls } from '../accountOp/accountOp'
import { Call } from '../accountOp/types'
import { getErrorCodeStringFromReason } from '../errorDecoder/helpers'

const erc20interface = new Interface(ERC20.abi)

export const BROADCAST_OPTIONS = {
  bySelf: 'self', // standard txn
  bySelf7702: 'self7702', // executeBySender
  byBundler: 'bundler', // userOp
  byRelayer: 'relayer', // execute
  byOtherEOA: 'otherEOA', // execute + standard
  delegation: 'delegation' // txn type 4
}

async function waitBeforeRetry(chainId: bigint) {
  // block time at ethereum is bigger, so we wait 3s per failures
  await wait(chainId === 1n ? 3000 : 1500)
}

export function getByOtherEOATxnData(
  account: Account,
  op: AccountOp,
  accountState: AccountOnchainState
): { to: Hex; value: bigint; data: Hex } {
  if (accountState.isDeployed) {
    const ambireAccount = new Interface(AmbireAccount.abi)
    return {
      to: op.accountAddr as Hex,
      value: 0n,
      data: ambireAccount.encodeFunctionData('execute', [getSignableCalls(op), op.signature]) as Hex
    }
  }

  const ambireFactory = new Interface(AmbireFactory.abi)
  return {
    to: account.creation!.factoryAddr as Hex,
    value: 0n,
    data: ambireFactory.encodeFunctionData('deployAndExecute', [
      account.creation!.bytecode,
      account.creation!.salt,
      getSignableCalls(op),
      op.signature
    ]) as Hex
  }
}

// How many times a failing estimation is retried before we give up and fall back
// to the batch-wide simulated gas limit. The retry only exists to give the RPC a
// chance to catch up with the pending state of the previous txn in the batch; the
// caller now has a safe fallback, so there is no point spinning for ~16s (the old
// limit of 10) and stalling the broadcast.
const MAX_ESTIMATE_RETRIES = 3

// estimate the gas for the call
async function estimateGas(
  provider: RPCProvider,
  from: string,
  call: Call,
  nonce: number,
  chainId: bigint,
  // The failure that caused the previous attempt. `error` is set only when the RPC
  // itself failed; `retryReason` describes the non-error cases (a stale pending
  // state). Passing a non-Error through `error` used to make the thrown message
  // read `Failed estimating gas for broadcast:` with nothing after the colon.
  lastFailure?: { error?: Error; retryReason?: string },
  counter: number = 0
): Promise<bigint> {
  // this should happen only in the case of internet issues
  if (counter > MAX_ESTIMATE_RETRIES) {
    const errorReason = lastFailure?.error
      ? getErrorCodeStringFromReason(lastFailure.error.message)
      : ''
    const reason = errorReason || (lastFailure?.retryReason ? ` ${lastFailure.retryReason}` : '')
    throw new Error(`Failed estimating gas for broadcast${reason ? `:${reason}` : ''}`)
  }

  const callEstimateGas = provider
    .send('eth_estimateGas', [
      {
        from,
        to: call.to,
        value: toQuantity(call.value),
        data: call.data,
        nonce: toQuantity(nonce)
      },
      'pending'
    ])
    .catch((e) => e)
  const callGetNonce = provider.getTransactionCount(from).catch(() => null)
  const [gasLimit, foundNonce] = await Promise.all([callEstimateGas, callGetNonce])

  // imagine a batch with two swaps, 4 txns total. Both swaps have the same from token
  // and from token amount. So #1 & #3 is an approval. #2 spends the approval.
  // when it's time to estimate #3, if the RPC doesn't know about #2, it will return
  // a lower gas for the transaction as the old state hasn't spent the approval =>
  // no storage writing. This results in an out of gas error on the #3 txn broadacst.
  // To fix this, we ensure there's no nonce discrepancy upon broadcast, meaning
  // the RPC knows about the previous txn that spends the approval, hence returning
  // the correct gasLimit for the call
  //
  // Only a nonce that runs AHEAD of the chain means "the RPC hasn't seen the
  // previous txn yet" — that is the case retrying can fix. A nonce that lags
  // behind (`foundNonce > nonce`) means the nonce we were handed is stale: the txn
  // would be rejected as "nonce too low" and no amount of waiting changes that, so
  // it is reported immediately instead of spinning through every retry.
  // `foundNonce` is null only when the lookup itself failed — with nothing to
  // compare against, don't invent a discrepancy.
  if (foundNonce !== null && nonce < foundNonce)
    throw new Error(
      `Failed estimating gas for broadcast: the nonce ${nonce} is behind the chain (the RPC reports ${foundNonce})`
    )

  let hasNonceDiscrepancyOnApproval = foundNonce !== null && nonce > foundNonce
  if (hasNonceDiscrepancyOnApproval) {
    try {
      hasNonceDiscrepancyOnApproval =
        call.data !== '0x' && !!erc20interface.decodeFunctionData('approve', call.data)
    } catch (e) {
      hasNonceDiscrepancyOnApproval = false
    }
  }

  // if there's an error, wait a bit and retry
  // the error is most likely because of an incorrect RPC pending state
  if (gasLimit instanceof Error || hasNonceDiscrepancyOnApproval) {
    // if the gasLimit is throwing because the smart account is returning INSUFFICIENT_PRIVILEGE,
    // return the error without retrying
    if (gasLimit instanceof Error && gasLimit.message.includes('INSUFFICIENT_PRIVILEGE'))
      throw gasLimit

    const failure =
      gasLimit instanceof Error
        ? { error: gasLimit }
        : {
            retryReason: `the RPC has not seen the previous txn yet (nonce ${nonce}, RPC reports ${foundNonce})`
          }
    // The raw error never reaches the user (`getErrorCodeStringFromReason` drops
    // the ones it cannot make sense of), so log it before it is lost.
    // eslint-disable-next-line no-console
    console.warn(
      `[broadcast] gas estimation attempt ${counter + 1} failed for ${call.to}:`,
      failure.error ?? failure.retryReason
    )

    await waitBeforeRetry(chainId)
    return estimateGas(provider, from, call, nonce, chainId, failure, counter + 1)
  }

  // add a 10% overhead to prevent OOG
  return BigInt(gasLimit) + BigInt(gasLimit) / 10n
}

/**
 * Estimate a single call of a batch, falling back to the batch-wide simulated gas
 * limit when the RPC cannot produce an estimate.
 *
 * A failed estimation must never kill an otherwise valid broadcast. The calls of a
 * batch are broadcast with sequential nonces, so the chain executes them in order
 * regardless of what the RPC's pending state looks like; the only thing at stake
 * here is the gas limit of this one txn. `simulatedGasLimit` covers the whole batch
 * and is therefore an over-estimate for a single call — and a gas limit is a cap,
 * not a charge, so overshooting costs nothing while undershooting is an OOG.
 */
async function estimateGasOrFallback(
  provider: RPCProvider,
  from: string,
  call: Call,
  nonce: number,
  chainId: bigint,
  fallback: bigint
): Promise<bigint> {
  try {
    return await estimateGas(provider, from, call, nonce, chainId)
  } catch (e: any) {
    // An unauthorized signer is not a stale-state problem: the txn is going to
    // revert on chain no matter what gas limit we give it, so let it stop the
    // broadcast instead of burning the user's gas on a doomed txn.
    if (e?.message?.includes('INSUFFICIENT_PRIVILEGE')) throw e

    // eslint-disable-next-line no-console
    console.warn(
      `[broadcast] falling back to the simulated gas limit (${fallback}) for ${call.to}:`,
      e?.message ?? e
    )
    return fallback
  }
}

export async function getTxnData(
  account: Account,
  op: AccountOp,
  accountState: AccountOnchainState,
  provider: RPCProvider,
  broadcastOption: string,
  nonce: number,
  call?: Call
): Promise<{ to: Hex | undefined; value: bigint; data: Hex; gasLimit?: bigint }> {
  if (account.safeCreation) {
    const safeData = getSafeBroadcastTxn(op, accountState)
    return {
      ...safeData,
      gasLimit: (op.gasFeePayment as GasFeePayment).simulatedGasLimit
    }
  }

  // no need to estimate gas for delegation, it's already estimated
  if (broadcastOption === BROADCAST_OPTIONS.delegation) {
    if (op.calls.length > 1) {
      const ambireAccount = new Interface(AmbireAccount.abi)
      return {
        to: account.addr as Hex,
        value: 0n,
        data: ambireAccount.encodeFunctionData('executeBySender', [getSignableCalls(op)]) as Hex
      }
    }

    if (!call) throw new Error('single txn broadcast misconfig')
    return {
      to: call.to as Hex | undefined,
      value: call.value,
      data: call.data as Hex,
      gasLimit: (op.gasFeePayment as GasFeePayment).simulatedGasLimit
    }
  }

  if (broadcastOption === BROADCAST_OPTIONS.bySelf) {
    if (!call) throw new Error('single txn broadcast misconfig')

    // if the accountOp has more than 1 calls, we have to calculate the gas
    // for each one separately
    let gasLimit: bigint | undefined = (op.gasFeePayment as GasFeePayment).simulatedGasLimit
    if (op.calls.length > 1) {
      gasLimit = await estimateGasOrFallback(
        provider,
        account.addr,
        call,
        nonce,
        op.chainId,
        gasLimit
      )
    }

    const singleCallTxn = {
      to: call.to as Hex | undefined,
      value: call.value,
      data: call.data as Hex,
      gasLimit
    }

    return singleCallTxn
  }

  if (broadcastOption === BROADCAST_OPTIONS.byOtherEOA) {
    const otherEOACall = getByOtherEOATxnData(account, op, accountState)
    const gasLimit = await estimateGasOrFallback(
      provider,
      (op.gasFeePayment as GasFeePayment).paidBy,
      otherEOACall,
      nonce,
      op.chainId,
      (op.gasFeePayment as GasFeePayment).simulatedGasLimit
    )
    return { ...otherEOACall, gasLimit }
  }

  // 7702 executeBySender
  const ambireAccount = new Interface(AmbireAccount.abi)
  return {
    to: account.addr as Hex,
    value: 0n,
    data: ambireAccount.encodeFunctionData('executeBySender', [getSignableCalls(op)]) as Hex
  }
}

export async function buildRawTransaction(
  account: Account,
  op: AccountOp,
  accountState: AccountOnchainState,
  provider: RPCProvider,
  network: Network,
  nonce: number,
  broadcastOption: string,
  call?: Call
): Promise<TxnRequest> {
  const gasFeePayment = op.gasFeePayment as GasFeePayment

  const txnData = await getTxnData(
    account,
    op,
    accountState,
    provider,
    broadcastOption,
    nonce,
    call
  )
  const rawTxn: TxnRequest = {
    chainId: network.chainId,
    nonce,
    gasLimit: gasFeePayment.simulatedGasLimit,
    ...txnData,
    ...(gasFeePayment.isCustomGasLimit ? { gasLimit: gasFeePayment.simulatedGasLimit } : {})
  }

  if (gasFeePayment.maxPriorityFeePerGas !== undefined) {
    rawTxn.maxFeePerGas = gasFeePayment.gasPrice
    rawTxn.maxPriorityFeePerGas = gasFeePayment.maxPriorityFeePerGas
    rawTxn.type = 2
  } else {
    rawTxn.gasPrice = gasFeePayment.gasPrice
    rawTxn.type = 0
  }

  return rawTxn
}

export async function broadcastTransaction(
  provider: RPCProvider,
  signedTx: string,
  chainId: bigint,
  counter: number = 0
): Promise<TransactionResponse> {
  if (counter > 2) throw new Error('broadcast failed')
  try {
    return provider.broadcastTransaction(signedTx)
  } catch (e) {
    console.log('broadcast failed: ', e)
    await waitBeforeRetry(chainId)
    return broadcastTransaction(provider, signedTx, chainId, counter + 1)
  }
}
